import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { thumbSrcSet } from "../../data/games";
import type { Game } from "../../types/games";
import { type Feed, planPrices, priceSummary, salesOpen } from "../../hooks/useCatalog";
import { productPath } from "../../seo/productSeo";

/** "Deadlock External + Internal" -> "Deadlock": the artwork already names the build. */
export const shortName = (g: Game) => g.name.split(" ")[0];

/* ---- the lineup, straight under the hero ----
   The homepage ran 5000px without showing a single game or its price: the
   one thing a visitor came to find was behind "Explore products". Four tiles
   say what is on offer and what it costs before any of the pitch below. */
export const GameTile = ({ game, feed, index }: { game: Game; feed: Feed | null; index: number }) => {
  const open = salesOpen(game, feed);
  const entry = priceSummary(planPrices(game, feed))?.entry;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.5, delay: index * 0.07 }}
    >
      <Link
        to={productPath(game.id)}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
          e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
        }}
        className="card-spotlight edge-teal group relative block overflow-hidden rounded-2xl bg-ground-2 transition-transform duration-300 hover:-translate-y-1"
      >
        <div className="relative aspect-video overflow-hidden bg-[#0c0e13]">
          {game.dropdownImage && (
            <img
              src={game.dropdownImage}
              srcSet={thumbSrcSet(game)}
              sizes="(min-width: 1024px) 272px, 46vw"
              width={1376}
              height={768}
              alt={`${game.name} preview`}
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
              loading="lazy"
              decoding="async"
            />
          )}
          <div className="absolute inset-0 bg-linear-to-t from-ground-2 via-transparent to-transparent" />
        </div>
        <div className="flex items-center justify-between gap-3 px-4 pb-4 pt-2">
          <span className="min-w-0">
            <span className="flex items-baseline gap-1.5 font-semibold text-white">
              {shortName(game)}
              {game.badge === "Beta" && (
                <span className="text-[0.6rem] font-bold uppercase tracking-wider text-accent-pink">Beta</span>
              )}
            </span>
            {open && entry ? (
              <span className="block whitespace-nowrap text-xs text-white/60">
                from <span className="font-semibold text-white">{entry.display}</span>/{entry.unit}
                {entry.was && <s className="ml-1 text-white/40">{entry.was}</s>}
              </span>
            ) : (
              <span className="block whitespace-nowrap text-xs text-amber-300/90">Purchases paused</span>
            )}
          </span>
          {/* From sm up only: two tiles a row on a phone leave the price no
              room beside it, and the whole tile is the link anyway. */}
          <span className="hidden sm:grid h-8 w-8 shrink-0 place-items-center rounded-full border border-white/10 text-primary-light transition-colors duration-300 group-hover:border-primary group-hover:bg-primary group-hover:text-[#06211f]">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

